import React, { useEffect, useMemo, useState, lazy, createElement } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, LockIcon, CheckCircleIcon, BrainIcon, AwardIcon, LaptopIcon, DollarSignIcon, GraduationCapIcon } from 'lucide-react';
import { getUniversityBySlug, getProgramBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
// Form value → API value mappings
const educationToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    'high-school': 1,
    'some-high-school': 2,
    ged: 3,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[v] || 9;
};
const startTimeframeToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '4-6-months': 3,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[v] || 6;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};
export function AdvertiserTemplatePage() {
  const {
    collegeSlug,
    programSlug
  } = useParams<{
    collegeSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const initialProgram = university && programSlug ? getProgramBySlug(university, programSlug) : null;
  // Capture optional tracking params from the URL once at mount.
  // sub1/sub2/sub3 + the rest of the documented optional fields can all
  // be passed in via query string and will be forwarded on submit.
  const trackingParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      sub1: q.get('sub1') || '',
      sub2: q.get('sub2') || '',
      sub3: q.get('sub3') || '',
      userIP: q.get('userIP') || '',
      webInitiatingURL: q.get('webInitiatingURL') || document.referrer || '',
      webLandingURL: q.get('webLandingURL') || window.location.href,
      inventoryType: q.get('inventoryType') || '',
      mediaType: q.get('mediaType') || '1'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Inject LeadID + TrustedForm scripts AFTER first paint so they don't
  // compete with the initial render. They still load early enough to
  // observe user form interaction and produce a valid certificate.
  useEffect(() => {
    let leadIdScript: HTMLScriptElement | null = null;
    let tfScript: HTMLScriptElement | null = null;
    const inject = () => {
      ;
      (window as any).myCallbackFunction = function (token: string) {
        const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
        if (field) field.setAttribute('value', token);
      };
      leadIdScript = document.createElement('script');
      leadIdScript.id = 'LeadiDscript_campaign';
      leadIdScript.type = 'text/javascript';
      leadIdScript.async = true;
      leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
      document.body.appendChild(leadIdScript);
      tfScript = document.createElement('script');
      tfScript.type = 'text/javascript';
      tfScript.async = true;
      tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape('false') + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
      document.body.appendChild(tfScript);
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    const handle = ric ? ric(inject, {
      timeout: 2000
    }) : window.setTimeout(inject, 1200) as unknown as number;
    return () => {
      const cancelIdle = (window as any).cancelIdleCallback as ((h: number) => void) | undefined;
      if (ric && cancelIdle) cancelIdle(handle);else window.clearTimeout(handle);
      try {
        leadIdScript?.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript?.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  const metaDescription = university ? initialProgram ? `Explore the ${initialProgram.degreeLevel ? `${initialProgram.degreeLevel} ` : ''}${initialProgram.name} program at ${university.name} and request information about your next steps.` : `Explore online degree programs at ${university.name} and request information about options that align with your educational goals.` : 'The requested university program could not be found. Explore accredited online degree programs on FindOnlineDegrees.';
  useMetaTags({
    title: university ? `${university.name} | Request Information` : 'Program Not Found',
    description: metaDescription,
    ogImage: university?.landingContent?.communityImage?.url,
    ogType: 'website',
    // Partner advertiser pages are paid lead-gen surfaces; keep them out
    // of the index so they don't compete with the brand site for SEO.
    robots: 'noindex, follow'
  });
  // Campus-aware advertisers (e.g. SJVC) use a step 0 to look up the user's
  // campus by zip before showing program options.
  const hasCampusFlow = Boolean(university?.campusZipLookup);
  const firstStep = hasCampusFlow ? 0 : 1;
  const totalSteps = hasCampusFlow ? 5 : 4;
  const [step, setStep] = useState(firstStep);
  const [formData, setFormData] = useState({
    degreeLevel: initialProgram?.degreeLevel || '',
    areaOfStudy: initialProgram?.category || '',
    program: initialProgram?.name || '',
    highestEducation: '',
    startDate: '',
    hsGradYear: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
    consentSms: false,
    campusId: 0,
    campusName: ''
  });
  const [error, setError] = useState('');
  const [campusLookupError, setCampusLookupError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Auto-advance Step 2
  useEffect(() => {
    if (step === 2 && formData.highestEducation && formData.startDate && formData.hsGradYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, formData.startDate, formData.hsGradYear, step]);
  // ZIP Code Lookup
  useEffect(() => {
    if (formData.zipCode.length === 5) {
      fetch(`https://api.zippopotam.us/us/${formData.zipCode}`).then(res => {
        if (res.ok) return res.json();
        throw new Error('Invalid zip');
      }).then(data => {
        if (data.places && data.places.length > 0) {
          setFormData(prev => ({
            ...prev,
            city: data.places[0]['place name'],
            state: data.places[0]['state abbreviation']
          }));
        }
      }).catch(() => {
        // Handle error silently
      });
    }
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-516" data-mp="2gctsq19aa9t8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center" data-id="element-517" data-mp="2gahenz6ncjlg">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-518" data-mp="27c5fh8rpsdhe">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-519" data-mp="22dax27xnsqxt">
            We couldn't find the specific program or university you're looking
            for.
          </p>
          <button onClick={() => navigate('/')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-520" data-mp="1xf93uu6irb8m">
            Return Home
          </button>
        </div>
      </div>;
  }
  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = () => {
    if (step === 0) {
      if (!formData.zipCode || formData.zipCode.length !== 5) {
        setCampusLookupError('Please enter your 5-digit ZIP code to continue.');
        return;
      }
      if (!formData.campusId) {
        setCampusLookupError(`Sorry, that ZIP code isn't served by a ${university.shortName} campus. Please check your ZIP and try again.`);
        return;
      }
      const campusPrograms = university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
      const preselectStillValid = !!initialProgram && campusPrograms.some(p => p.name === initialProgram.name);
      setFormData(prev => ({
        ...prev,
        degreeLevel: preselectStillValid ? initialProgram!.degreeLevel || '' : '',
        areaOfStudy: preselectStillValid ? initialProgram!.category : '',
        program: preselectStillValid ? initialProgram!.name : ''
      }));
      setCampusLookupError('');
      setStep(1);
      return;
    }
    if (step === 1) {
      if (university.singleProgramSelect) {
        if (!formData.program) {
          setError('Please select a program to continue.');
          return;
        }
      } else if (!formData.degreeLevel || !formData.areaOfStudy || !formData.program) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.highestEducation || !formData.startDate || !formData.hsGradYear) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!formData.city || !formData.state) {
        setError('Please enter a valid ZIP code.');
        return;
      }
      if (!formData.streetAddress.trim()) {
        setError('Please enter your street address.');
        return;
      }
      setStep(4);
    }
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) return formData.phone;
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };
  const handleSubmit = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!formData.consent) {
      setError('Please agree to the consent terms.');
      return;
    }
    if (university.tcpaTextSms && !formData.consentSms) {
      setError('Please check both consent boxes to continue.');
      return;
    }
    setIsSubmitting(true);
    // Read tracking values populated by the LeadID + TrustedForm scripts
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const trustedFormUrl = tfInput?.value || '';
    // Build the full lead payload per advertiser parameter spec.
    // Includes: required advertiser identifiers, mapped form values,
    // PII, and optional tracking parameters.
    const payload: Record<string, string> = {
      // Required advertiser identifiers. URL params (mPubGroupID, or
      // mPubID + mPubCampaignID) override the per-university config values
      // when present; otherwise fall back to the university config.
      ...getPublisherParams(location.search, {
        mPubID: university.mPubID || '',
        mPubCampaignID: university.mPubCampaignID || ''
      }),
      mBrandID: university.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: `s-${generateUUID()}`,
      leadIdToken,
      // Mapped form values. When the partner provides a numeric program
      // catalog (programId), post that ID as mProgram; otherwise fall back
      // to the program name.
      degreeInterest: formData.degreeLevel,
      areaStudy: formData.areaOfStudy,
      mProgram: (() => {
        const matched = programsForThisFlow.find(p => p.name === formData.program);
        if (matched?.mProgramValue) return matched.mProgramValue;
        return matched?.programId ? String(matched.programId) : formData.program;
      })(),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      gradYear: formData.hsGradYear,
      // PII
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      campusLocation: formData.campusId ? String(formData.campusId) : '',
      smsConsent: formData.consentSms ? '1' : '0',
      // Optional tracking
      useragent: navigator.userAgent,
      sub1: trackingParams.sub1,
      sub2: trackingParams.sub2,
      sub3: trackingParams.sub3,
      userIP: trackingParams.userIP,
      webInitiatingURL: trackingParams.webInitiatingURL,
      webLandingURL: trackingParams.webLandingURL,
      trustedFormUrl,
      inventoryType: trackingParams.inventoryType,
      deviceType: getDeviceType(),
      mediaType: trackingParams.mediaType
    };
    console.log('[Advertiser Lead] payload:', payload);
    // Host-and-post: the advertiser provides a posting endpoint per partner.
    // Configure it via university.leadPostUrl. If unset, no post is fired
    // (the payload is still logged above for QA / verification).
    const postBase = university.leadPostUrl || '';
    if (postBase) {
      const qs = new URLSearchParams(payload).toString();
      const fullUrl = `${postBase}?${qs}`;
      console.log('[Advertiser Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Advertiser Lead] post error:', err);
      }
    } else {
      console.log('[Advertiser Lead] No leadPostUrl configured for this advertiser — skipping post.');
    }
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: formData.program,
        content_category: formData.areaOfStudy
      });
    }
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }, 1000);
  };
  // Derived program list — honors campus-aware advertisers (filters to the
  // programs available at the user's matched campus).
  const programsForThisFlow = hasCampusFlow && formData.campusId && university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
  // Derived options for Step 1
  const uniqueAreas = Array.from(new Set(programsForThisFlow.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel).map(p => p.category)));
  const availablePrograms = programsForThisFlow.filter(p => (!formData.degreeLevel || p.degreeLevel === formData.degreeLevel) && (!formData.areaOfStudy || p.category === formData.areaOfStudy));
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 60
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-521" data-mp="3rnv3dnvh5amt">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center" data-id="element-522" data-mp="99vmwwywd33j">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-523" data-mp="4bu329tnczr0">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-524" data-mp="j7a10pxclfso" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-525" data-mp="e8p2qw1np66v">
            Thanks, {formData.firstName}!
          </h2>
          <p className="text-gray-600" data-id="element-526" data-mp="2l0pwc04dritz">
            {university.name} will reach out shortly. Redirecting you to the
            homepage...
          </p>
        </div>
      </div>;
  }
  // Dynamic hero title — show the selected program once chosen, otherwise a generic prompt.
  // On the first step (entry view), prefer the partner's high-CTR landing headline if provided.
  const isEntryStep = step === firstStep;
  // Once the user has chosen both a program and a degree level, surface the
  // degree in the H1 (e.g. "Bachelor's in Early Childhood Education").
  const selectedProgramTitle = formData.program && formData.degreeLevel ? `${formData.degreeLevel} in ${formData.program}` : formData.program || initialProgram?.name;
  const heroTitle = isEntryStep && university.landingContent ? university.landingContent.headline : selectedProgramTitle || `Find Your ${university.shortName} Program`;
  // Show the fuller landing subheadline on every step (entry and beyond) so
  // the program-selection / later steps aren't left with just the short
  // mission line. Falls back to the mission when no landing copy exists.
  const heroDescription = university.landingContent?.subheadline !== undefined ? university.landingContent.subheadline : university.mission;
  const featureIconMap: Record<string, any> = {
    BrainIcon,
    AwardIcon,
    LaptopIcon,
    DollarSignIcon,
    GraduationCapIcon
  };
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-527" data-mp="b8zts766g4k9">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-528" data-mp="6b82nfdcwmh4" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-529" data-mp="1a28re7ymt60" />

      {/* White top nav bar with centered logo */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-530" data-mp="3tfe8478cygj6">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-531" data-mp="3oft7zthgc24v">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-532" data-mp="1rlyh398cyzno" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-533" data-mp="2fx6gxajxn5r3">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-534" data-mp="266lk9sntkbat">
            <LockIcon className="w-3.5 h-3.5" data-id="element-535" data-mp="217qzil6akco2" />
            Secure
          </div>
        </div>
      </header>

      {/* Branded main wrapper — neutral surface for the supporting sections */}
      <main className="flex-grow bg-white" data-id="element-536" data-mp="1sb9pjl9nyktw">
        {/* HERO — full-bleed brand color, optionally overlaid with a partner image */}
        <section className="relative py-12 md:py-20 px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-537" data-mp="1ib91jm0ungjd">
          {/* Mobile-only eyebrow + H1 — rendered ABOVE the grid so the form
           can sit directly beneath it and stay above the fold on phones.
           Desktop renders the eyebrow + H1 inside the left column instead. */}
          <div className="relative max-w-6xl mx-auto lg:hidden text-white mb-6" data-id="element-538" data-mp="3u05ssinhj5lt">
            <p className="text-sm font-semibold mb-3 text-white/90" data-id="element-539" data-mp="2r8d62x62p2r">
              {university.name}
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight" data-id="element-540" data-mp="31bi5khj6e65m">
              {heroTitle}
            </h1>
          </div>

          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-541" data-mp="3b6jsd6pzaw51">
            {/* Left: Branded hero copy — appears BELOW the form on mobile,
             beside it on desktop. */}
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-542" data-mp="qh2scr4ykpq6">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid). */}
              <p className="hidden lg:block text-base font-semibold mb-4 text-white/90" data-id="element-543" data-mp="2risjm53erno4">
                {university.name}
              </p>
              <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-544" data-mp="2mk8ww2gzq7up">
                {heroTitle}
              </h1>
              {heroDescription && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-545" data-mp="2wgkdh4nm5juy">
                  {heroDescription}
                </p>}
              {university.landingContent?.heroHighlights && university.landingContent.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-546" data-mp="2hnidp69w2w52">
                    {university.landingContent.heroHighlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-547" data-mp="1p4lbytvqmmay">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-548" data-mp="1azkvgmyh7jg9" />
                        <span data-id="element-549" data-mp="1fqb5welrecht">{h}</span>
                      </li>)}
                  </ul>}
            </div>

            {/* Right: Form card — appears directly under the H1 on mobile
             (above the fold), to the right on desktop. */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10 order-1 lg:order-2" data-id="element-550" data-mp="15ydrz03d96fe">
              {/* Progress Indicator */}
              <div className="mb-6" data-id="element-551" data-mp="1ynufpkg1jz5a">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-552" data-mp="1ymt3hcnah6lz">
                  {university.formHeading || 'Take the next step in your education.'}
                </h2>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-553" data-mp="1ot2hceemx89g">
                  <div className="h-full transition-all duration-300" style={{
                  width: `${(step - firstStep + 1) / totalSteps * 100}%`,
                  backgroundColor: university.ctaColor
                }} data-id="element-554" data-mp="8b6dcj1p22zg" />
                </div>
              </div>

              {step > firstStep && <button onClick={() => {
              setStep(step - 1);
              setError('');
            }} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-555" data-mp="ju77h18ud8bi">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-556" data-mp="17i6v3sauwilv" />
                  <span className="text-sm font-medium" data-id="element-557" data-mp="17gnfm3x0ybl5">Back</span>
                </button>}

              {/* Step 0: Campus lookup by zip (campus-aware advertisers only) */}
              {step === 0 && hasCampusFlow && <div className="space-y-6" data-id="element-558" data-mp="3nae26i9a3v85">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-559" data-mp="3nb5vtnjbfivs">
                    Enter your ZIP code to see programs available at your{' '}
                    {university.shortName} campus.
                  </p>

                  <div data-id="element-560" data-mp="22rtpwt7fzhwj">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-561" data-mp="22rak2av2gjyv">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setCampusLookupError('');
                  // Auto-resolve campus when the zip becomes valid.
                  // If no match, leave the dropdown alone so the
                  // user can still pick manually.
                  if (val.length === 5 && university.campusZipLookup) {
                    const match = university.campusZipLookup(val);
                    if (match) {
                      setFormData(prev => ({
                        ...prev,
                        zipCode: val,
                        campusId: match.id,
                        campusName: match.name
                      }));
                      return;
                    }
                  }
                  updateField('zipCode', val);
                }} placeholder="e.g. 93291" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-lg" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-562" data-mp="27plryg1swkeg" />
                  </div>

                  <div data-id="element-563" data-mp="1qqlnnhb18ybn">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-564" data-mp="1qrdl6xo6x64k">
                      Campus
                    </label>
                    <select value={formData.campusId || ''} disabled aria-readonly="true" tabIndex={-1} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700 appearance-none cursor-not-allowed" style={selectStyle as any} data-id="element-565" data-mp="2ecnx0cxbm23a">
                      <option value="" data-id="element-566" data-mp="1velbw97w8par">
                        -- Enter ZIP to match a campus --
                      </option>
                      {(university.landingContent?.campusList || []).map(c => <option key={c.id} value={c.id} data-id="element-567" data-mp="1mm9sh7b7624a">
                            {c.name} Campus
                          </option>)}
                    </select>
                    <p className="mt-2 text-xs text-gray-500" data-id="element-568" data-mp="1cpoie7kbm0tl">
                      Your campus is automatically determined by your ZIP code.
                    </p>
                  </div>

                  {campusLookupError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-569" data-mp="1j4dwni8qor31">
                      <p className="text-red-600 text-sm" data-id="element-570" data-mp="1e5suvedu9zch">
                        {campusLookupError}
                      </p>
                    </div>}
                </div>}

              {/* Step 1: Program Selection — single dropdown variant */}
              {step === 1 && university.singleProgramSelect && <div className="space-y-6" data-id="element-571" data-mp="10f77bcdfx189">
                  <div data-id="element-572" data-mp="vfb77o7v5txz">
                    <label htmlFor="program-select" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-573" data-mp="vg431u5aaz1c">
                      Program of Interest
                    </label>
                    <select id="program-select" value={formData.program} onChange={e => {
                  const selected = programsForThisFlow.find(p => p.name === e.target.value);
                  updateField('program', e.target.value);
                  updateField('degreeLevel', selected?.degreeLevel || '');
                  updateField('areaOfStudy', selected?.category || '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-574" data-mp="lhwxs8kcy5f6">
                      <option value="" data-id="element-575" data-mp="3aylnbn8ydnvp">-- Select Program --</option>
                      {programsForThisFlow.map(p => <option key={p.name} value={p.name} data-id="element-576" data-mp="2o9dlcrncx9kq">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 1: Program Selection */}
              {step === 1 && !university.singleProgramSelect && <div className="space-y-6" data-id="element-577" data-mp="20i86aep8qlbh">
                  <div data-id="element-578" data-mp="25a9cpjzq9htl">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-579" data-mp="25ark9crubigt">
                      Degree Level
                    </label>
                    <select value={formData.degreeLevel} onChange={e => {
                  updateField('degreeLevel', e.target.value);
                  updateField('areaOfStudy', '');
                  updateField('program', '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-580" data-mp="1qjsba0n3erwp">
                      <option value="" data-id="element-581" data-mp="1lvswdzc4gda9">-- Select Degree Level --</option>
                      {university.degreeLevels.map(level => <option key={level} value={level} data-id="element-582" data-mp="1gw6idnogyt0e">
                          {level}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-583" data-mp="1qq6nvqnny09n">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-584" data-mp="24n29eil3pk6l">
                      Area of Study
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', e.target.value);
                  updateField('program', '');
                }} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-585" data-mp="1zqbs5rxk8ijl">
                      <option value="" data-id="element-586" data-mp="1tz93ipialyn5">-- Select Area of Study --</option>
                      {uniqueAreas.map(area => <option key={area} value={area} data-id="element-587" data-mp="3vsghnvm66a7g">
                          {area}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-588" data-mp="3jiqwemphqxy5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-589" data-mp="3ji7fn9bkuflp">
                      Program
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', e.target.value)} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-590" data-mp="3ehbbcunbcrfr">
                      <option value="" data-id="element-591" data-mp="2hv7yaw77008b">-- Select Program --</option>
                      {availablePrograms.map(p => <option key={p.name} value={p.name} data-id="element-592" data-mp="eb5i0hmf70r5">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 2: Academic Background */}
              {step === 2 && <div className="space-y-6" data-id="element-593" data-mp="san7kz7jii1k">
                  <div data-id="element-594" data-mp="sbq17ew8yyl6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-595" data-mp="x9zsheeupf1i">
                      Highest Level of Education
                    </label>
                    <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-596" data-mp="j9yty5m26jp7">
                      <option value="" data-id="element-597" data-mp="35hd4zc4cemdj">-- Please Select --</option>
                      <option value="some-high-school" data-id="element-598" data-mp="2hs20pknsa9un">Some High School</option>
                      <option value="high-school" data-id="element-599" data-mp="2hpxyr89g3kx0">
                        High School Diploma/GED
                      </option>
                      <option value="some-college" data-id="element-600" data-mp="2rhll4ur5abrm">Some College</option>
                      <option value="associates" data-id="element-601" data-mp="2rjy6zj00x69y">Associate's Degree</option>
                      <option value="bachelors" data-id="element-602" data-mp="2mpkd5l10f77e">Bachelor's Degree</option>
                      <option value="masters" data-id="element-603" data-mp="1z0iwxblxqrrk">Master's Degree</option>
                      <option value="doctorate" data-id="element-604" data-mp="1u2prwlwdc3sv">Doctorate</option>
                    </select>
                  </div>

                  <div data-id="element-605" data-mp="2jreqx4jt2gms">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-606" data-mp="2jptwrg1ekzpg">
                      When would you like to start classes?
                    </label>
                    <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-607" data-mp="2sind7gsl654w">
                      <option value="" data-id="element-608" data-mp="3l8dxy8oq459z">-- Please Select --</option>
                      <option value="immediately" data-id="element-609" data-mp="3q6p3w8bwdvmx">Immediately</option>
                      <option value="1-3-months" data-id="element-610" data-mp="3q4ukwzk6sdlh">1-3 months</option>
                      <option value="3-6-months" data-id="element-611" data-mp="1eiajf29jw36n">3-6 months</option>
                      <option value="6-12-months" data-id="element-612" data-mp="19kit2cok0p4k">6-12 months</option>
                      <option value="1-year-plus" data-id="element-613" data-mp="19mby5njir1ag">1+ year</option>
                      <option value="undecided" data-id="element-614" data-mp="14s7rz6i2uxaf">Undecided</option>
                    </select>
                  </div>

                  <div data-id="element-615" data-mp="zu5ydrn0zfkp">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-616" data-mp="zslc0pav72xx">
                      High School Graduation Year
                    </label>
                    <select value={formData.hsGradYear} onChange={e => updateField('hsGradYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-617" data-mp="qs6gyy83m9h8">
                      <option value="" data-id="element-618" data-mp="1bc6wtfm9msj1">-- Please Select --</option>
                      {years.map(year => <option key={year} value={year} data-id="element-619" data-mp="1fcqr8bfgfi1h">
                          {year}
                        </option>)}
                      <option value="before-1965" data-id="element-620" data-mp="11fu1yeezi8mp">Before 1965</option>
                    </select>
                  </div>
                </div>}

              {/* Step 3: Location */}
              {step === 3 && <div className="space-y-6" data-id="element-621" data-mp="weeeygt8xs1o">
                  {formData.city && formData.state ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-622" data-mp="2mrdd0gf8duxo">
                      <span className="text-green-600 text-xl" data-id="element-623" data-mp="2qsywikm9t7gu">📍</span>
                      <div className="flex-1" data-id="element-624" data-mp="f5bv5pwc3k9y">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-625" data-mp="f654mymognvs">
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                  updateField('city', '');
                  updateField('state', '');
                  updateField('zipCode', '');
                }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-626" data-mp="owq1agisjic2">
                        change
                      </button>
                    </div> : <div data-id="element-627" data-mp="12stqtwipz2i7">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-628" data-mp="39l4bkri85scl">
                        ZIP Code
                      </label>
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('zipCode', val);
                }} placeholder="e.g. 90210" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-629" data-mp="34nueqqsiaeeq" />
                    </div>}

                  {formData.city && formData.state && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-630" data-mp="2hk5ga7ekl2c2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-631" data-mp="314mhtw36ki0d">
                        Street Address
                      </label>
                      <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-632" data-mp="361mnw7015krh" />
                    </div>}
                </div>}

              {/* Step 4: Contact & Submit */}
              {step === 4 && <div className="space-y-6" data-id="element-633" data-mp="9ywyija92cvx">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-634" data-mp="9zptkmzujdy8">
                    Where should {university.shortName} send your information?
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-635" data-mp="9n0kmsq5zzw">
                    <div data-id="element-636" data-mp="3rf6e7veak9c1">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-637" data-mp="3rfoa2qx1kljf">
                        First Name
                      </label>
                      <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-638" data-mp="suwsfzmsfqi5" />
                    </div>
                    <div data-id="element-639" data-mp="1j6i1u8yh6ee6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-640" data-mp="1j7t4g5g6pg3p">
                        Last Name
                      </label>
                      <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-641" data-mp="1swt6jxpzgrm4" />
                    </div>
                  </div>

                  <div data-id="element-642" data-mp="hi64s3nglvgr">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-643" data-mp="3id58314d8wrw">
                      Email Address
                    </label>
                    <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-644" data-mp="3dd92ixg0sxa8" />
                  </div>

                  <div data-id="element-645" data-mp="34m0g8l78p8s4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-646" data-mp="34lgusuids29c">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-647" data-mp="2gu1u16695t07" />
                  </div>

                  <div className="pt-4 space-y-3" data-id="element-648" data-mp="1f3a7n2o6uks3">
                    {university.tcpaTextSms && <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-649" data-mp="114alc1o5ehz3">
                        <input type="checkbox" checked={formData.consentSms} onChange={e => updateField('consentSms', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-650" data-mp="3luta1oj3x9e5" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-651" data-mp="1hohzwgitwu8b">
                          {university.tcpaTextSms}
                        </span>
                      </label>}
                    <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-652" data-mp="18qfu7no5hdep">
                      <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-653" data-mp="yr8ggzdi7gr2" />
                      <span className="text-xs text-gray-600 leading-relaxed" data-id="element-654" data-mp="3aetqouhoo1kg">
                        {university.tcpaText || `By checking this box, I consent to receive calls and text messages from ${university.name} at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.`}
                      </span>
                    </label>
                  </div>
                </div>}

              {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-655" data-mp="6wk0wf18kvc8">
                  <p className="text-red-600 text-sm" data-id="element-656" data-mp="buln38tylarx">{error}</p>
                </div>}

              <div className="mt-8" data-id="element-657" data-mp="x0gwevsoio67">
                {step < 4 ? <button onClick={handleContinue} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-658" data-mp="sz54gjmc1quu">
                    Continue
                  </button> : <button onClick={handleSubmit} disabled={isSubmitting || !formData.consent || Boolean(university.tcpaTextSms) && !formData.consentSms} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-659" data-mp="1br68s1hvm9bn">
                    {isSubmitting ? 'Submitting...' : university.submitLabel || 'Submit Application'}
                  </button>}
              </div>
            </div>
          </div>
        </section>
        {/* /HERO */}

        {/* Supporting site content — renders on every step so the
            program page (deep link) reads as a full marketing site,
            not just a form. */}
        {university.landingContent && <>
            {/* Stats strip */}
            {university.landingContent.stats && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-660" data-mp="34lfnrfi60yej">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-661" data-mp="38nsrxzh9ko97">
                  {university.landingContent.stats.map(s => <div key={s.label} className="text-center" data-id="element-662" data-mp="f57kw5n099mu">
                      <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
                color: university.primaryColor
              }} data-id="element-663" data-mp="k6wztwdcm7g7">
                        {s.value}
                      </div>
                      <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-664" data-mp="xx0cbvmfemsg">
                        {s.label}
                      </div>
                    </div>)}
                </div>
              </section>}

            {/* Why us — feature grid */}
            <section className="bg-gray-50 py-16 md:py-24 px-4" data-id="element-665" data-mp="1bv72sqp1pki3">
              <div className="max-w-6xl mx-auto" data-id="element-666" data-mp="39oe42glh1sxg">
                <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-667" data-mp="3doob7wl02pdw">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-668" data-mp="3doy17k61owjl">
                    Why {university.shortName}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight" data-id="element-669" data-mp="2wflph04j27jj">
                    Education built around your career goals.
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-670" data-mp="3p3820xvqgmij">
                  {university.landingContent.features.map(f => {
                const Icon = featureIconMap[f.icon] || GraduationCapIcon;
                return <div key={f.title} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow" data-id="element-671" data-mp="3b36s6sxhq4qx">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                    backgroundColor: `${university.ctaColor}15`
                  }} data-id="element-672" data-mp="1zni4u0gbr3pl">
                          <Icon className="w-7 h-7" style={{
                      color: university.ctaColor
                    }} data-id="element-673" data-mp="1s50p3chuuso6" />
                        </div>
                        <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900 mb-2" data-id="element-674" data-mp="1n6z58pclupbn">
                          {f.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-675" data-mp="18fqegcwyuxwx">
                          {f.description}
                        </p>
                      </div>;
              })}
                </div>
              </div>
            </section>

            {/* Info sections — alternating layout */}
            {university.landingContent.infoSections && university.landingContent.infoSections.map((s, i) => <section key={s.heading} className={`py-16 md:py-24 px-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-676" data-mp="fwedbuzth6ea" data-mpi={i}>
                  <div className="max-w-5xl mx-auto" data-id="element-677" data-mp="pt0zcn3vq3hw">
                    {s.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
              color: university.ctaColor
            }} data-id="element-678" data-mp="13sif6cz766f4">
                        {s.eyebrow}
                      </p>}
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5 max-w-3xl" data-id="element-679" data-mp="18l3bpm9hmxmc">
                      {s.heading}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-680" data-mp="1hl85uupo7zik">
                      {s.body}
                    </p>
                    {s.bullets && <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl" data-id="element-681" data-mp="3gyym7171m1kv">
                        {s.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-700" data-id="element-682" data-mp="3lsl1700oihju">
                            <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                  color: university.ctaColor
                }} data-id="element-683" data-mp="2t34b490j7cqs" />
                            <span className="text-sm md:text-base" data-id="element-684" data-mp="371u8lpr9jefo">{b}</span>
                          </li>)}
                      </ul>}
                  </div>
                </section>)}

            {/* Community image — partner-supplied lifestyle photo */}
            {university.landingContent.communityImage && <section className="bg-white py-16 md:py-24 px-4" data-id="element-685" data-mp="rg73itm009ve">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-686" data-mp="rgyv7tch5nge">
                  <div className="order-2 md:order-1" data-id="element-687" data-mp="mn44i5ygc87t">
                    {university.landingContent.communityImage.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-688" data-mp="dpbot0otiyjw">
                        {university.landingContent.communityImage.eyebrow}
                      </p>}
                    {university.landingContent.communityImage.heading && <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-689" data-mp="1y3ufe2dtlo7r">
                        {university.landingContent.communityImage.heading}
                      </h2>}
                    {university.landingContent.communityImage.body && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-690" data-mp="3sdsmvn0ksf6p">
                        {university.landingContent.communityImage.body}
                      </p>}
                  </div>
                  <div className="order-1 md:order-2 flex justify-center" data-id="element-691" data-mp="a74f1pe55dxc">
                    <img src={university.landingContent.communityImage.url} alt={university.landingContent.communityImage.alt || university.name} loading="lazy" decoding="async" className="w-full max-w-md object-contain" data-id="element-692" data-mp="a4s3btyz7zsq" />
                  </div>
                </div>
              </section>}

            {/* Testimonial */}
            {university.landingContent.testimonial && <section className="py-16 md:py-24 px-4" style={{
          backgroundColor: `${university.primaryColor}08`
        }} data-id="element-693" data-mp="2igknenrgv8m0">
                <div className="max-w-3xl mx-auto text-center" data-id="element-694" data-mp="2sal0bbszpi5a">
                  <div className="text-6xl leading-none mb-4 font-serif" style={{
              color: university.primaryColor
            }} data-id="element-695" data-mp="2nevyt2xetr6c">
                    "
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed mb-6" data-id="element-696" data-mp="29djlkz9mw3o5">
                    {university.landingContent.testimonial.quote}
                  </blockquote>
                  <div className="text-sm text-gray-600" data-id="element-697" data-mp="1gwz0mwpi7y5i">
                    <span className="font-bold text-gray-900" data-id="element-698" data-mp="1gvfjl3w0xj2o">
                      {university.landingContent.testimonial.name}
                    </span>
                    {' — '}
                    {university.landingContent.testimonial.program}
                  </div>
                </div>
              </section>}

            {/* Career section with image */}
            {university.landingContent.careerSection && <section className="bg-white py-16 md:py-24 px-4" data-id="element-699" data-mp="15cwm8ezybzcl">
                <div className="max-w-6xl mx-auto rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-700" data-mp="15a0mqm8gh2m1">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-701" data-mp="1ed1oybu7a9kf">
                    <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-702" data-mp="1eaozldm44wxc">
                      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-703" data-mp="1j4js96jp4m8k">
                        {university.landingContent.careerSection.headline}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-704" data-mp="1szuyt1kyan58">
                        {university.landingContent.careerSection.body}
                      </p>
                      <button onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} className="self-start px-7 py-3 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg" style={{
                  backgroundColor: university.ctaColor
                }} data-id="element-705" data-mp="3hmwqyeq10tmc">
                        Get Started
                      </button>
                    </div>
                    <div className="min-h-[280px] md:min-h-0" data-id="element-706" data-mp="3mkohp5exq9oy">
                      <img src={university.landingContent.careerSection.imageUrl} alt={university.landingContent.careerSection.imageAlt || university.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-707" data-mp="3mk58cd3cotmj" />
                    </div>
                  </div>
                </div>
              </section>}

            {/* Final CTA band */}
            {university.landingContent.finalCta && <section className="py-16 md:py-20 px-4" style={{
          backgroundColor: university.primaryColor
        }} data-id="element-708" data-mp="1jhemh6j30t4t">
                <div className="max-w-4xl mx-auto text-center text-white" data-id="element-709" data-mp="19rv0vt3zil69">
                  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-710" data-mp="19ps1wjkuthpe">
                    {university.landingContent.finalCta.headline}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-711" data-mp="229qlpkefveaq">
                    {university.landingContent.finalCta.body}
                  </p>
                  <button onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl" style={{
              backgroundColor: university.ctaColor,
              color: '#fff'
            }} data-id="element-712" data-mp="3g5ifsrai80yz">
                    {university.landingContent.finalCta.buttonLabel || 'Request Information'}
                  </button>
                </div>
              </section>}
          </>}

        {/* Branded footer */}
        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-713" data-mp="loy4kh2ubz74">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-714" data-mp="qmzwlri8v8uq">
            <p data-id="element-715" data-mp="qnj5kipx2mwm">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-716" data-mp="2hrbd9mhgcc8a">
              {university.accreditation} • Founded {university.founded}
            </p>
          </div>
        </footer>
      </main>
    </div>;
}